/**
 * Objectives & Tasks (Career OS) — types for Target → Objectives → Milestones → Tasks.
 * Kanban statuses, categories, and API payloads.
 */

export type TargetStatus = 'active' | 'achieved' | 'paused' | 'dropped';

export type ObjectiveStatus = 'not_started' | 'active' | 'paused' | 'completed' | 'dropped';

export type ObjectiveCategory =
  | 'skill'
  | 'resume'
  | 'networking'
  | 'applications'
  | 'interview_prep'
  | 'mindset'
  | 'custom';

export type Priority = 'low' | 'medium' | 'high';

export type MilestoneStatus = 'on_track' | 'at_risk' | 'delayed' | 'completed';

export type TaskStatus =
  | 'backlog'
  | 'planned'
  | 'in_progress'
  | 'blocked'
  | 'completed'
  | 'abandoned';

export type NotificationType =
  | 'task_due_soon'
  | 'task_overdue'
  | 'task_blocked_long'
  | 'milestone_soon'
  | 'objective_at_risk'
  | 'objective_completed'
  | 'nudge_no_progress'
  | 'nudge_almost_done'
  | 'calendar_reminder_due'
  | 'payment_verification_failed'
  /** Agent Assisted Handoff — bundle ready, popup blocked, save errors, etc. */
  | 'apply_handoff'
  /** Prompt user to confirm after returning from the job portal. */
  | 'apply_confirm_needed'
  /** User confirmed they applied on the portal. */
  | 'apply_recorded'
  /** General Agent / canvas feedback (saved resume opened, download, etc.). */
  | 'agent_info'
  /** Admin-only: auto-generated blog draft ready for QA. */
  | 'daily_brief'
  | 'blog_draft_created'
  /** Admin-only: blog draft unpublished 24h+ (reminder). */
  | 'blog_draft_stale_reminder'
  /** Admin-only: blog auto-published (solo mode — no manual QA). */
  | 'blog_auto_published';

export interface UserTarget {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  start_date: string | null;
  target_date: string | null;
  status: TargetStatus;
  created_at: string;
  updated_at: string;
}

export interface CareerObjective {
  id: string;
  user_id: string;
  target_id: string | null;
  title: string;
  description: string | null;
  category: ObjectiveCategory;
  priority: Priority;
  start_date: string | null;
  end_date: string | null;
  status: ObjectiveStatus;
  success_metric: string | null;
  created_at: string;
  updated_at: string;
}

export interface CareerMilestone {
  id: string;
  objective_id: string;
  title: string;
  start_date: string | null;
  end_date: string | null;
  status: MilestoneStatus;
  completion_percentage: number;
  created_at: string;
  updated_at: string;
}

export interface CareerTask {
  id: string;
  user_id: string;
  objective_id: string | null;
  milestone_id: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: Priority;
  due_date: string | null;
  estimated_time_minutes: number | null;
  actual_time_minutes: number | null;
  is_repeatable: boolean;
  blocked_reason: string | null;
  blocked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskActivityLogEntry {
  id: string;
  task_id: string;
  user_id: string;
  action: 'created' | 'moved' | 'completed' | 'blocked' | 'unblocked' | 'abandoned' | 'updated';
  old_status: string | null;
  new_status: string | null;
  created_at: string;
}

export interface UserNotification {
  id: string;
  user_id: string;
  type: NotificationType;
  entity_type: 'task' | 'objective' | 'milestone' | 'target' | 'calendar_reminder' | 'payment' | 'job_application' | 'blog_post' | 'daily_brief';
  entity_id: string | null;
  message: string;
  is_read: boolean;
  created_at: string;
}

export interface UserCalendarReminder {
  id: string;
  user_id: string;
  title: string;
  note: string | null;
  entry_date: string;
  reminder_date: string;
  notification_created_at: string | null;
  created_at: string;
}

export interface ExecutionHealth {
  activeObjectivesCount: number;
  tasksDueToday: number;
  overdueCount: number;
  blockedCount: number;
  weeklyCompletionPercent: number;
}

export const TASK_STATUS_ORDER: TaskStatus[] = [
  'backlog',
  'planned',
  'in_progress',
  'blocked',
  'completed',
  'abandoned',
];
